import { api } from "_shared/_stores/api";
import Guid from "_shared/guids/guid";
import AdministratorInvitationSchema, {
  AdministratorInvitationJSON,
} from "../_schema/administrator-invitation-schema";
import z from "zod";
import AdministratorInvitation from "../administrator-invitation";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import Session from "users/__session/session";
import AuthInfoSchema, { AuthInfoJSON } from "users/auth/info/auth-info-schema";
import { AuthInfo } from "users/auth/info/auth-info";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";

export const administratorInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region GET
    getAdministratorInvitationById: build.query<AdministratorInvitationJSON, Guid>({
      query: (id) => ({ 
        url: `/legal-entities/admin-invitations/${id}`
      }),
      rawResponseSchema: AdministratorInvitationSchema.AdministratorInvitationJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, id) => [{ type: "AdministratorInvitation", id: id.value }],
    }),
    getAdminInvitationsForLegalEntity: build.query<AdministratorInvitationJSON[], Guid>({
      query: (legalEntityId) => ({
        url: `/legal-entities/admin-invitations?legalEntityId=${legalEntityId}`
      }),
      rawResponseSchema: z.array(AdministratorInvitationSchema.AdministratorInvitationJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, _error, legalEntityId) => 
        result
        ? [
            { type: "AdministratorInvitation", id: `LegalEntity-${legalEntityId}` },
            ...result.map(invitation => ({ type: "AdministratorInvitation" as const, id: invitation.id }))
          ]
        : [{ type: "AdministratorInvitation", id: `LegalEntity-${legalEntityId}` }],
    }),
    // #endregion

    // #region POST
    acceptAdministratorInvitation: build.mutation<AuthInfoJSON, {invitation: AdministratorInvitation, session: Session, request: AcceptEntityUserInvitationAPIRequest}>({
      query: ({invitation, request}) => ({
        url: `/legal-entities/admin-invitations/${invitation.id}/accept`,
        method: "POST",
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json-patch+json' }
      }),
      rawResponseSchema: AuthInfoSchema.AuthInfoJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async ({session}, {queryFulfilled}) => {
        const response = await queryFulfilled;
        if(!response.meta?.response?.ok)
        {
          return;
        }
        const authInfo = new AuthInfo(AuthInfoSchema.deserialize(response.data));
        session.initializeFromAuthResponse(authInfo.toAuthResponse());
      },
      invalidatesTags: (_result, _error, {invitation}) => [
        { type: "AdministratorInvitation", id: invitation.id.value },
        {type: "AdministratorInvitation", id: `LegalEntity-${invitation.entity.id}`}
      ]
    }),
    // #endregion
  })
});

export const {
  useGetAdministratorInvitationByIdQuery,
  useLazyGetAdministratorInvitationByIdQuery,
  useGetAdminInvitationsForLegalEntityQuery,
  useLazyGetAdminInvitationsForLegalEntityQuery,
  useAcceptAdministratorInvitationMutation
} = administratorInvitationApi;